import React, { useState } from "react";
import { useFormik } from "formik";
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles,
} from "@material-ui/core/styles";
import { CircularProgress, Dialog, DialogProps, Grid } from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import Typography from "@material-ui/core/Typography";

import { ButtonCancel, ButtonSubmit, InputForm } from "components/shared";
import { PageHeader } from "components/shared/PageHeader";
import { toast } from "react-toastify";
import { changePassword } from "services/sessionService";
import { passwordValidate } from "utils";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      alignItems: "center",
      margin: 0,
      padding: theme.spacing(3),
      marginBottom: "-50px",
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  });

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(3),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(3),
  },
}))(MuiDialogActions);

interface DialogDetailsProps extends DialogProps {
  open: boolean;
  flag?: boolean;
  setOpen: (value: boolean) => void;
  maxWidth?: false | "md" | "xs" | "sm" | "lg" | "xl" | undefined;
}

type SetPass = {
  newPassword: string;
  confirmNewPassword: string;
}

const setNewPass: SetPass = {
  newPassword: "",
  confirmNewPassword: "",
}

export default function DialogNewPass({
  open,
  flag,
  setOpen,
  maxWidth = "xs",
}: DialogDetailsProps) {
  const formik = useFormik({
    initialValues: setNewPass,
    onSubmit: (values) => {
      const { newPassword, confirmNewPassword } = values;
      if ( !!newPassword && !!confirmNewPassword ) {
        if ( newPassword === confirmNewPassword ) return setPassword({ newPassword, confirmNewPassword });
        if ( newPassword !== confirmNewPassword ) toast.error("As senhas devem ser iguais")
      } else {
        toast.error("Preencha os campos obrigatórios")
      }
    },
  });
  
  const handleClose = () => {
    setOpen(false);
  };

  const setPassword = async ({ newPassword, confirmNewPassword }: SetPass) => {
    const passwordValid = passwordValidate(newPassword)
    if (!passwordValid) {
      toast.error("A senha deve conter 6 a 10 caracteres com letras maiúsculas e minúsculas, números e caracteres especiais como . + - [ ] * ~ _ @ # : ? e sem espaços")
    } else {
      const password = { PSW: newPassword.replaceAll(" ", "") }
      try {
        await changePassword(password)
          .then(() => setOpen(false))
          .then(() => {
            formik.setFieldValue("newPassword", "")
            formik.setFieldValue("confirmNewPassword", "")
          })
          .finally(() => toast.success("A senha foi alterada com sucesso"))
      } catch (err) {
        console.log(err);
        toast.error("Não foi possível realizar a operação");
      }
    }
  }

  const handleSubmit = (e: any) => {
    formik.handleSubmit(e)
  };
  return (
    <Dialog
      BackdropProps={{
        style: {
          backgroundColor: 'rgba(255, 255, 255, .95)'
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={open}
      maxWidth={maxWidth}
    >
      <DialogTitle id="customized-dialog-title" onClose={handleClose}>
        <PageHeader title="Alterar senha" showUserData={false} />
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} justifyContent="space-between">
          <Grid item xs={12}>
            <InputForm
              type="password"
              textTransform="inherit"
              value={formik.values.newPassword}
              onChange={formik.handleChange}
              error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
              helperText={formik.touched.newPassword && formik.errors.newPassword}
              label="DIGITE A NOVA SENHA"
              id="newPassword"
              name="newPassword"
              maxLength="10"
              variant="outlined"
              fullWidth
            />
          </Grid>

          <Grid item>
            <Typography style={{
              fontSize:'12px',
              fontWeight:'500'
            }}>
              A senha deve conter 6 a 10 caracteres com letras maiúsculas e minúsculas, números e caracteres especiais como . + - [ ] * ~ _ @ # : ? e sem espaços.
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <InputForm
              type="password"
              textTransform="inherit"
              value={formik.values.confirmNewPassword}
              onChange={formik.handleChange}
              error={formik.touched.confirmNewPassword && Boolean(formik.errors.confirmNewPassword)}
              helperText={formik.touched.confirmNewPassword && formik.errors.confirmNewPassword}
              label="CONFIRME A NOVA SENHA"
              id="confirmNewPassword"
              name="confirmNewPassword"
              maxLength="10"
              variant="outlined"
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {flag && 
          <ButtonCancel onClick={() => handleClose()}>
            Cancelar
          </ButtonCancel>
        }
        <ButtonSubmit onClick={(e) => handleSubmit(e)} color="primary">
          Enviar
        </ButtonSubmit>
      </DialogActions>
    </Dialog>
  );
}
